import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Chess, type Square } from "chess.js";
import { Chessboard } from "react-chessboard";
import type { DemocracyProposalDto, Side } from "@chess/shared";

export type Promotion = "q" | "r" | "b" | "n";

export interface BoardProps {
  position: string;
  orientation: Side;
  /** Whether this user may move right now. */
  interactive: boolean;
  /** Last move in UCI, e.g. "e2e4" — highlighted so you can see the reply. */
  lastMoveUci?: string | undefined;
  /** Ballots from a resolved democracy round; active-round ballots stay private. */
  voteProposals?: readonly DemocracyProposalDto[];
  /** True after a result commits; adds an unobtrusive on-board dismissal cue. */
  voteResolved?: boolean;
  /** The dismissal cue is onboarding only; vote arrows remain visible without it. */
  showVoteHint?: boolean;
  onDismissVoteOverlay?: () => void;
  onMove: (from: string, to: string, promotion?: Promotion) => boolean;
}

// Lichess-style affordances: a filled dot means an empty destination, a ring
// means something is there to take.
const SELECTED = { background: "var(--sq-selected)" };
const LAST_MOVE = { background: "var(--sq-last)" };
const CHECK = { background: "var(--sq-check)", borderRadius: "50%" };
const MOVE_DOT = {
  background: "radial-gradient(circle, var(--sq-dot) 22%, transparent 24%)",
};
const CAPTURE_RING = {
  background: "radial-gradient(circle, transparent 54%, var(--sq-dot) 56%)",
  borderRadius: "50%",
};

export function Board({
  position,
  orientation,
  interactive,
  lastMoveUci,
  voteProposals = [],
  voteResolved = false,
  showVoteHint = true,
  onDismissVoteOverlay,
  onMove,
}: BoardProps) {
  const wrapper = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(420);
  const [selected, setSelected] = useState<string | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<{ from: string; to: string } | null>(
    null,
  );

  // react-chessboard needs a pixel width, so track the container instead of
  // guessing — this is what keeps the board usable on a phone.
  useEffect(() => {
    const element = wrapper.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured) setWidth(Math.floor(measured));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const chess = useMemo(() => {
    try {
      return new Chess(position);
    } catch {
      return null;
    }
  }, [position]);

  // Drop the selection whenever the position changes underneath us, so a
  // highlight can't linger over a square whose piece has moved.
  useEffect(() => {
    setSelected(null);
    setPendingPromotion(null);
  }, [position]);

  const movesFromSelected = useMemo(() => {
    if (!chess || !selected) return [];
    try {
      return chess.moves({ square: selected as Square, verbose: true });
    } catch {
      return [];
    }
  }, [chess, selected]);

  const isPromotion = useCallback(
    (from: string, to: string) => {
      if (!chess) return false;
      return chess
        .moves({ square: from as Square, verbose: true })
        .some((move) => move.to === to && move.promotion);
    },
    [chess],
  );

  /** Runs a move, opening the promotion dialog first when one is needed. */
  const attemptMove = useCallback(
    (from: string, to: string) => {
      if (isPromotion(from, to)) {
        setPendingPromotion({ from, to });
        return false;
      }
      return onMove(from, to);
    },
    [isPromotion, onMove],
  );

  const handleSquareClick = useCallback(
    (square: string) => {
      if (!interactive || !chess) return;

      if (selected) {
        if (square === selected) {
          setSelected(null);
          return;
        }
        if (movesFromSelected.some((move) => move.to === square)) {
          if (attemptMove(selected, square)) setSelected(null);
          return;
        }
      }

      // Selecting is only meaningful for a piece that actually has moves.
      const piece = chess.get(square as Square);
      const ownPiece = piece && piece.color === chess.turn();
      setSelected(ownPiece ? square : null);
    },
    [interactive, chess, selected, movesFromSelected, attemptMove],
  );

  const squareStyles = useMemo(() => {
    const styles: Record<string, Record<string, string>> = {};

    if (lastMoveUci) {
      styles[lastMoveUci.slice(0, 2)] = LAST_MOVE;
      styles[lastMoveUci.slice(2, 4)] = LAST_MOVE;
    }

    if (chess?.inCheck()) {
      const turn = chess.turn();
      for (const row of chess.board()) {
        for (const piece of row) {
          if (piece?.type === "k" && piece.color === turn) styles[piece.square] = CHECK;
        }
      }
    }

    if (selected) {
      styles[selected] = SELECTED;
      for (const move of movesFromSelected) {
        // `captured` covers en passant, where the target square looks empty.
        styles[move.to] = move.captured ? CAPTURE_RING : MOVE_DOT;
      }
    }

    return styles;
  }, [lastMoveUci, chess, selected, movesFromSelected]);

  const voteMoves = useMemo(() => {
    const moves = new Map<string, { uci: string; san: string; usernames: string[]; chosen: boolean }>();
    for (const proposal of voteProposals) {
      const existing = moves.get(proposal.uci) ?? {
        uci: proposal.uci,
        san: proposal.san,
        usernames: [],
        chosen: false,
      };
      existing.usernames.push(proposal.username);
      existing.chosen ||= proposal.chosen;
      moves.set(proposal.uci, existing);
    }
    const pathCounts = new Map<string, number>();
    for (const move of moves.values()) {
      const path = move.uci.slice(0, 4);
      pathCounts.set(path, (pathCounts.get(path) ?? 0) + 1);
    }
    const pathIndexes = new Map<string, number>();
    return [...moves.values()].map((move) => {
      const path = move.uci.slice(0, 4);
      const index = pathIndexes.get(path) ?? 0;
      pathIndexes.set(path, index + 1);
      const count = pathCounts.get(path) ?? 1;
      return { ...move, pathIndex: index, pathCount: count };
    });
  }, [voteProposals]);

  const voteArrows = useMemo(
    () =>
      voteMoves.map(
        (move) =>
          [
            move.uci.slice(0, 2) as Square,
            move.uci.slice(2, 4) as Square,
            move.chosen ? "rgba(63, 114, 176, 0.88)" : "rgba(214, 170, 99, 0.72)",
          ] as [Square, Square, string],
      ),
    [voteMoves],
  );

  function displayPosition(square: string) {
    const file = square.charCodeAt(0) - "a".charCodeAt(0);
    const rank = Number(square[1]) - 1;
    const column = orientation === "white" ? file : 7 - file;
    const row = orientation === "white" ? 7 - rank : rank;
    return { x: (column + 0.5) * 12.5, y: (row + 0.5) * 12.5 };
  }

  function votePathLayout(move: (typeof voteMoves)[number]) {
    const from = displayPosition(move.uci.slice(0, 2));
    const to = displayPosition(move.uci.slice(2, 4));
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    const offsetSquares = (move.pathIndex - (move.pathCount - 1) / 2) * 0.24;
    const offset = offsetSquares * 12.5;
    const offsetX = (-dy / length) * offset;
    const offsetY = (dx / length) * offset;
    const midpoint = { x: from.x + dx / 2 + offsetX, y: from.y + dy / 2 + offsetY };
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    return {
      hitStyle: {
        left: `${from.x + offsetX}%`,
        top: `${from.y + offsetY}%`,
        width: `${length}%`,
        transform: `translateY(-50%) rotate(${angle}deg)`,
      } satisfies CSSProperties,
      countStyle: { transform: `translate(-50%, -50%) rotate(${-angle}deg)` } satisfies CSSProperties,
      tooltipStyle: { left: `${midpoint.x}%`, top: `${midpoint.y}%` } satisfies CSSProperties,
      opensBelow: midpoint.y < 37.5,
    };
  }

  return (
    <div className="board" ref={wrapper}>
      <Chessboard
        position={position}
        boardWidth={width}
        boardOrientation={orientation}
        arePiecesDraggable={interactive}
        areArrowsAllowed={false}
        customArrows={voteArrows}
        customSquareStyles={squareStyles}
        customDarkSquareStyle={{ backgroundColor: "var(--board-dark)" }}
        customLightSquareStyle={{ backgroundColor: "var(--board-light)" }}
        customBoardStyle={{ borderRadius: "8px" }}
        onSquareClick={handleSquareClick}
        onPieceDrop={(from, to) => {
          setSelected(null);
          // Position is controlled by the parent. Returning false prevents the
          // board from visually committing a democracy ballot before it wins.
          void attemptMove(from, to);
          return false;
        }}
        // Driven manually so click-to-move gets the same dialog as dragging.
        showPromotionDialog={pendingPromotion !== null}
        promotionToSquare={(pendingPromotion?.to ?? null) as Square | null}
        onPromotionPieceSelect={(piece, from, to) => {
          const source = from ?? pendingPromotion?.from;
          const target = to ?? pendingPromotion?.to;
          setPendingPromotion(null);
          setSelected(null);
          // No piece means the dialog was dismissed.
          if (!piece || !source || !target) return false;
          return onMove(source, target, piece[1]!.toLowerCase() as Promotion);
        }}
      />
      {voteMoves.length > 0 ? (
        <div className="board__vote-layer" aria-live={voteResolved ? "polite" : undefined}>
          {voteResolved && showVoteHint ? (
            <div className="board__vote-hint">
              <span>Vote resolved</span>
              <span><kbd>Space</kbd> toggles</span>
              <button type="button" onClick={onDismissVoteOverlay} aria-label="Dismiss vote overlay">
                Dismiss <kbd>Esc</kbd>
              </button>
            </div>
          ) : null}
          {voteMoves.map((move) => {
            const layout = votePathLayout(move);
            return (
            <div key={move.uci} className="board__vote-arrow-zone">
              <button
                type="button"
                className="board__vote-arrow-hit"
                style={layout.hitStyle}
                aria-label={`${move.usernames.length} vote${move.usernames.length === 1 ? "" : "s"} for ${move.san}: ${move.usernames.join(", ")}`}
                aria-describedby={`vote-detail-${move.uci}`}
              >
                <span className={`board__vote-count${move.chosen ? " board__vote-count--chosen" : ""}`} style={layout.countStyle}>
                  {move.usernames.length}
                </span>
              </button>
              <div
                id={`vote-detail-${move.uci}`}
                className={`board__vote-tooltip${layout.opensBelow ? " board__vote-tooltip--below" : ""}`}
                style={layout.tooltipStyle}
                role="tooltip"
              >
                <span>{move.chosen ? `Selected move · ${move.san}` : `Vote for ${move.san}`}</span>
                <strong>{move.usernames.join(" · ")}</strong>
              </div>
            </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
